import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSetSnackBar } from '../../../store/Snackbar.state';
import { Loading } from '../../atoms';
import { useGetTaxRateDetail, useUpdateTaxRate, UpdateTaxRateRequest } from '../../../hooks';

function EditTaxRate() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSnackbar = useSetSnackBar();
  const { mutate: getTaxRateDetail, isLoading: isDetailLoading } = useGetTaxRateDetail();
  const { mutate: updateTaxRate, isLoading: isUpdateLoading } = useUpdateTaxRate();

  const [formData, setFormData] = useState<UpdateTaxRateRequest>({
    id: 0,
    name: '',
    tax: 0,
  });

  const [taxInputValue, setTaxInputValue] = useState<string>('');

  const [error, setError] = useState('');
  const isLoading = isDetailLoading || isUpdateLoading;

  // Location'dan taxRateId'yi al
  const taxRateId = location.state?.taxRateId;

  useEffect(() => {
    if (taxRateId) {
      getTaxRateDetail(taxRateId, {
        onSuccess: (response: any) => {
          if (response?.isSuccess && response?.data) {
            const taxRateData = response.data;
            setFormData({
              id: taxRateData.id,
              name: taxRateData.name,
              tax: taxRateData.tax,
            });
            setTaxInputValue(taxRateData.tax === 0 ? '' : taxRateData.tax.toString());
          } else {
            setSnackbar({
              severity: "error",
              description: response?.message || "Vergi oranı bilgileri yüklenemedi",
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "Vergi oranı bilgileri yüklenirken hata oluştu",
            isOpen: true,
          });
        },
      });
    }
  }, [taxRateId, getTaxRateDetail, setSnackbar]);

  const handleInputChange = (field: keyof UpdateTaxRateRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (field === 'tax') {
      const value = event.target.value;
      setTaxInputValue(value);
      const numericValue = value === '' ? 0 : parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        [field]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value
      }));
    }
    setError('');
  };

  const handleSubmit = () => {
    setError('');

    if (!formData.name.trim()) {
      setError('Vergi oranı adı gereklidir');
      return;
    }

    if (formData.tax < 0) {
      setError('Vergi oranı negatif olamaz');
      return;
    }

    updateTaxRate(formData, {
      onSuccess: (data: any) => {
        if (data?.isSuccess) {
          setSnackbar({
            severity: "success",
            isOpen: true,
            description: data.message || "Vergi oranı başarıyla güncellendi",
          });
          navigate('/vuk-507/tax-rates-listing', { state: { refresh: true } });
        } else {
          setSnackbar({
            severity: "error",
            description: data?.message || "Güncelleme sırasında hata oluştu",
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "Güncelleme sırasında hata oluştu",
          isOpen: true,
        });
      },
    });
  };

  const handleCancel = () => {
    navigate('/vuk-507/tax-rates-listing');
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Vergi Oranı Düzenle
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID"
                value={formData.id}
                disabled
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vergi Oranı Adı"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vergi Oranı (%)"
                type="number"
                value={taxInputValue}
                onChange={handleInputChange('tax')}
                required
                variant="outlined"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  İptal
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  Kaydet
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

export default EditTaxRate;
